import { useEffect, useMemo, useRef, useState } from 'react';
import TeamLogo from './TeamLogo';
import { getTeamSchedule } from '../services/teamSchedule';
import { getTeamSeasonStats } from '../services/teamSeasonStats';
import '../styles/team-profile-hub.css';

const VALID_TABS = new Set(['overview', 'schedule', 'stats']);
const SCHEDULE_FILTERS = [
  { value: 'all', label: 'All games' },
  { value: 'completed', label: 'Completed' },
  { value: 'upcoming', label: 'Upcoming' },
];
const PLAYER_STAT_GROUPS = [
  {
    value: 'attack',
    label: 'Attack',
    columns: [
      { key: 'setsPlayed', label: 'SP', definition: 'sets played' },
      { key: 'kills', label: 'K', definition: 'kills' },
      { key: 'attackErrors', label: 'E', definition: 'attack errors' },
      { key: 'attackAttempts', label: 'TA', definition: 'attack attempts' },
      { key: 'hittingPercentage', label: 'PCT', definition: 'hitting percentage' },
    ],
  },
  {
    value: 'serve-defense',
    label: 'Serve / defense',
    columns: [
      { key: 'setsPlayed', label: 'SP', definition: 'sets played' },
      { key: 'serviceAces', label: 'SA', definition: 'service aces' },
      { key: 'serviceErrors', label: 'SE', definition: 'service errors' },
      { key: 'digs', label: 'D', definition: 'digs' },
      { key: 'receptionErrors', label: 'RE', definition: 'reception errors' },
    ],
  },
  {
    value: 'set-block',
    label: 'Set / block',
    columns: [
      { key: 'setsPlayed', label: 'SP', definition: 'sets played' },
      { key: 'assists', label: 'A', definition: 'assists' },
      { key: 'soloBlocks', label: 'BS', definition: 'solo blocks' },
      { key: 'blockAssists', label: 'BA', definition: 'block assists' },
      { key: 'points', label: 'PTS', definition: 'points' },
    ],
  },
];

function readInitialTab() {
  if (typeof window === 'undefined') return 'overview';
  const value = new URLSearchParams(window.location.search).get('teamTab');
  return VALID_TABS.has(value) ? value : 'overview';
}

function writeTab(tab) {
  const url = new URL(window.location.href);
  url.searchParams.delete('sport');
  if (tab === 'overview') url.searchParams.delete('teamTab');
  else url.searchParams.set('teamTab', tab);
  window.history.replaceState({}, '', url.toString());
}

function formatDate(value, includeYear = false) {
  const text = String(value || '').trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return text;
  const date = new Date(`${text}T12:00:00Z`);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: includeYear ? 'numeric' : undefined,
    timeZone: 'UTC',
  }).format(date);
}

function formatUpdated(value) {
  const text = String(value || '').trim();
  if (!text) return '';
  const iso = text.match(/^(\d{4}-\d{2}-\d{2})/);
  return iso ? formatDate(iso[1], true) : text.replace(/^Through\s+/i, '');
}

function formatTime(game) {
  const text = String(game?.timeLabel || game?.startTime || '').trim();
  if (game?.status === 'final' && (!text || /\b(?:TBA|TBD|final|scheduled)\b/i.test(text))) return '';
  if (!text || /scheduled/i.test(text)) return 'Time TBA';
  if (/final/i.test(text)) return 'Final';
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime()) && /T/.test(text)) {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(parsed);
  }
  return text;
}

function getRank(team) {
  return [team?.finalRank, team?.archivedRank, team?.rank]
    .find((value) => Number.isFinite(value)) ?? null;
}

function getRankDisplay(team, available) {
  if (!available) return 'Unavailable';
  const rank = getRank(team);
  if (!Number.isFinite(rank)) return 'Not ranked';
  return `${team?.finalRankTied || team?.archivedRankTied || team?.tied ? 'T-' : ''}${rank}`;
}

function getRecord(team) {
  return team?.finalRecord?.label || team?.record || 'Record unavailable';
}

function getFinish(team) {
  return team?.seasonFinish?.display || team?.seasonFinishDisplay ||
    team?.seasonFinish?.label || team?.seasonFinishLabel || 'Unavailable';
}

function getFinishDetail(team) {
  const finish = team?.seasonFinish;
  if (!finish) return '';
  return [
    finish.roundReached,
    finish.opponentName ? `${finish.result === 'win' ? 'defeated' : 'vs.'} ${finish.opponentName}` : '',
  ].filter(Boolean).join(' · ');
}

function ProfileMetric({ label, value, detail }) {
  return (
    <div className="team-profile-metric">
      <span>{label}</span>
      <strong>{value || '—'}</strong>
      {detail ? <small>{detail}</small> : null}
    </div>
  );
}

function MatchRow({ game, onOpen }) {
  const perspective = game?.teamPerspective || {};
  const scoreAvailable = (game?.status === 'final' || game?.status === 'live' || game?.live) && Number.isInteger(perspective.score) && Number.isInteger(perspective.opponentScore);
  const result = perspective.result;
  const site = game.neutralSite || perspective.site === 'neutral' || perspective.side === 'home' ? 'vs.' : 'at';
  const openable = Boolean(game?.matchCenterAvailable && game?.gameId);

  return (
    <button
      type="button"
      className="team-hub-match-row"
      disabled={!openable}
      onClick={() => openable && onOpen(game)}
      aria-label={openable ? `Open match against ${perspective.opponentName || 'opponent'}` : undefined}
    >
      <span className={`team-hub-result ${result || game?.status || 'scheduled'}`}>
        {result === 'win' ? 'W' : result === 'loss' ? 'L' : game?.live ? 'LIVE' : '—'}
      </span>
      <span className="team-hub-match-main">
        <strong>{site} {perspective.opponentName || 'Opponent TBA'}</strong>
        <small>{[formatDate(game?.date, true), formatTime(game), ['suspended', 'postponed', 'canceled', 'unconfirmed'].includes(game.status) ? game.statusLabel : ''].filter(Boolean).join(' · ')}</small>
      </span>
      <span className="team-hub-match-score">
        {scoreAvailable ? `${perspective.score}–${perspective.opponentScore}` : game?.live ? 'Live' : '—'}
      </span>
      <span className="team-hub-match-chevron" aria-hidden="true">›</span>
    </button>
  );
}

function MatchPanel({ title, eyebrow, games, loading, error, empty, onOpen }) {
  return (
    <section className="team-hub-match-panel">
      <div className="team-hub-panel-heading">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h2>{title}</h2>
        </div>
        <span>{loading || error ? '—' : games.length}</span>
      </div>
      {loading ? (
        <div className="team-hub-inline-state">Loading matches…</div>
      ) : error ? (
        <div className="team-hub-inline-state error">{error}</div>
      ) : games.length ? (
        <div className="team-hub-match-list">
          {games.map((game) => <MatchRow key={game.id || `${game.date}-${game.teamPerspective?.opponentName}`} game={game} onOpen={onOpen} />)}
        </div>
      ) : (
        <div className="team-hub-inline-state">{empty}</div>
      )}
    </section>
  );
}

function ScheduleTab({ schedule, loading, error, filter, onFilter, onOpen }) {
  const games = useMemo(() => {
    const source = schedule?.games || [];
    const newestFirst = (left, right) =>
      String(right?.date || '').localeCompare(String(left?.date || '')) ||
      String(right?.gameId || '').localeCompare(String(left?.gameId || ''));
    if (filter === 'completed') return source.filter((game) => game.final).sort(newestFirst);
    if (filter === 'upcoming') return source.filter((game) => ['scheduled', 'live'].includes(game.status));
    return [...source].sort(newestFirst);
  }, [schedule, filter]);

  const grouped = useMemo(() => {
    const output = [];
    games.forEach((game) => {
      const month = String(game.date || '').slice(0, 7);
      let group = output.at(-1);
      if (!group || group.key !== month) {
        const date = new Date(`${month}-01T12:00:00Z`);
        group = {
          key: month,
          label: Number.isNaN(date.getTime()) ? month : new Intl.DateTimeFormat('en-US', {
            month: 'long', year: 'numeric', timeZone: 'UTC',
          }).format(date),
          games: [],
        };
        output.push(group);
      }
      group.games.push(game);
    });
    return output;
  }, [games]);

  return (
    <section className="team-hub-tab-panel team-hub-schedule-panel" aria-labelledby="team-schedule-heading">
      <div className="team-hub-section-heading">
        <div>
          <p className="eyebrow">Full season</p>
          <h2 id="team-schedule-heading">Schedule</h2>
        </div>
        <small>{loading ? 'Loading…' : error ? 'Unavailable' : `${schedule?.counts?.total || 0} games`}</small>
      </div>
      <div className="team-hub-segmented" aria-label="Schedule filter">
        {SCHEDULE_FILTERS.map((option) => (
          <button
            type="button"
            key={option.value}
            className={filter === option.value ? 'active' : ''}
            aria-pressed={filter === option.value}
            onClick={() => onFilter(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {!loading && !error && schedule?.recordCheck?.expected && !schedule.recordCheck.complete ? (
        <div className="team-hub-schedule-note">
          NCAA currently supplies {schedule.recordCheck.schedule?.matches || 0} completed schedule
          results ({schedule.recordCheck.schedule?.label || '0-0'}), while the verified season record is{' '}
          {schedule.recordCheck.expected.label}. The profile record remains authoritative while NCAA’s
          schedule feed is incomplete.
        </div>
      ) : null}
      {loading ? <div className="team-hub-state">Loading the season schedule…</div> : null}
      {!loading && error ? <div className="team-hub-state error">{error}</div> : null}
      {!loading && !error && !games.length ? (
        <div className="team-hub-state">No {filter === 'all' ? '' : filter} matches are currently published for this season.</div>
      ) : null}
      {!loading && !error ? grouped.map((group) => (
        <div className="team-hub-schedule-group" key={group.key}>
          <h3>{group.label}</h3>
          <div className="team-hub-match-list">
            {group.games.map((game) => <MatchRow key={game.id} game={game} onOpen={onOpen} />)}
          </div>
        </div>
      )) : null}
    </section>
  );
}

function TeamStats({ stats }) {
  return (
    <div className="team-hub-team-stats">
      <div className="team-hub-stat-grid">
        {(stats?.teamStats || []).map((item) => (
          <div className="team-hub-stat-card" key={item.key}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
            {item.perSet ? <small>{item.perSet} per set</small> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function PlayerStats({ stats }) {
  const [query, setQuery] = useState('');
  const [statGroup, setStatGroup] = useState('attack');
  const availablePlayers = Array.isArray(stats?.players) ? stats.players : [];
  const activeGroup = PLAYER_STAT_GROUPS.find((group) => group.value === statGroup) ||
    PLAYER_STAT_GROUPS[0];
  const players = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return availablePlayers.filter((player) => !normalized ||
      String(player?.name || '').toLowerCase().includes(normalized) ||
      String(player.position || '').toLowerCase().includes(normalized));
  }, [availablePlayers, query]);

  if (!availablePlayers.length) {
    const publishedBoxScores = Number(stats?.coverage?.boxscoresAvailable) || 0;
    return (
      <div className="team-hub-state">
        {publishedBoxScores
          ? 'NCAA box scores were found, but their player rows could not be read. Team totals remain available.'
          : 'Player statistics are unavailable for this team’s selected season.'}
      </div>
    );
  }

  return (
    <div className="team-hub-player-stats">
      <label className="team-hub-player-search">
        <span>Search players</span>
        <input
          type="search"
          value={query}
          placeholder="Player or position"
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      <div className="team-hub-player-groups" aria-label="Player statistic category">
        {PLAYER_STAT_GROUPS.map((group) => (
          <button
            type="button"
            key={group.value}
            className={activeGroup.value === group.value ? 'active' : ''}
            aria-pressed={activeGroup.value === group.value}
            onClick={() => setStatGroup(group.value)}
          >
            {group.label}
          </button>
        ))}
      </div>
      <div className="team-hub-player-table-wrap">
        <table className="team-hub-player-table">
          <thead>
            <tr>
              <th>Player</th>
              {activeGroup.columns.map((column) => <th key={column.key}>{column.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {players.map((player) => (
              <tr key={`${player.name}-${player.jersey}`}>
                <th>
                  <strong>{player.name}</strong>
                  <small>{[player.jersey ? `#${player.jersey}` : '', player.position].filter(Boolean).join(' · ')}</small>
                </th>
                {activeGroup.columns.map((column) => (
                  <td key={column.key}>{formatPlayerValue(player, column.key)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!players.length ? (
        <div className="team-hub-inline-state">
          No players match “{query.trim()}”.
        </div>
      ) : null}
      <p className="team-hub-stat-key">
        {activeGroup.columns.map((column) => `${column.label} ${column.definition}`).join(' · ')}
      </p>
    </div>
  );
}

function formatPlayerValue(player, key) {
  const value = player?.[key];
  if (!Number.isFinite(value)) return '—';
  if (key === 'hittingPercentage') return value.toFixed(3).replace(/^0\./, '.');
  if (key === 'points') return Number.isInteger(value) ? String(value) : value.toFixed(1);
  return String(Math.round(value));
}

function StatsTab({ statsState, mode, onMode, onRetry, fullScheduleCount = 0 }) {
  const stats = statsState.data;
  const completedGames = Number(stats?.coverage?.completedGames) || 0;
  const scheduledGames = Math.max(
    Number(fullScheduleCount) || 0,
    Number(stats?.coverage?.scheduledGames) || 0,
    completedGames
  );
  const coveredGames = mode === 'players'
    ? Number(stats?.coverage?.gamesWithPlayerStats) || 0
    : Number(stats?.coverage?.gamesWithTeamStats) || 0;
  const hasTeamStats = Boolean(stats?.teamStats?.length);
  const hasPlayerStats = Boolean(stats?.players?.length);
  const hasSelectedStats = mode === 'players' ? hasPlayerStats : hasTeamStats;

  return (
    <section className="team-hub-tab-panel team-hub-stats-panel" aria-labelledby="team-stats-heading">
      <div className="team-hub-section-heading">
        <div><p className="eyebrow">Season totals</p><h2 id="team-stats-heading">Statistics</h2></div>
        {stats ? <small>{coveredGames} of {completedGames} completed matches covered · {scheduledGames} scheduled</small> : null}
      </div>
      <div className="team-hub-segmented two" aria-label="Statistics view">
        <button type="button" className={mode === 'team' ? 'active' : ''} aria-pressed={mode === 'team'} onClick={() => onMode('team')}>Team stats</button>
        <button type="button" className={mode === 'players' ? 'active' : ''} aria-pressed={mode === 'players'} onClick={() => onMode('players')}>Player stats</button>
      </div>
      {statsState.loading ? <div className="team-hub-state">Building season statistics from match box scores…</div> : null}
      {!statsState.loading && statsState.error ? <div className="team-hub-state error">{statsState.error}<button type="button" onClick={onRetry}>Retry statistics</button></div> : null}
      {!statsState.loading && !statsState.error && stats && !stats.coverage.gamesWithStats ? (
        <div className="team-hub-state">NCAA has not published usable box scores for this team’s selected season.</div>
      ) : null}
      {!statsState.loading && !statsState.error && stats?.coverage.gamesWithStats ? (
        <>
          {coveredGames < completedGames ? (
            <div className="team-hub-coverage-note">
              Partial coverage: {mode === 'players' ? 'player totals' : 'team totals'} include {coveredGames} of {completedGames} completed matches with usable NCAA box scores. Future matches are not counted as missing statistics.
            </div>
          ) : null}
          {mode === 'team' ? (
            hasSelectedStats
              ? <TeamStats stats={stats} />
              : <div className="team-hub-state">Team statistics are unavailable for this season.</div>
          ) : <PlayerStats stats={stats} />}
        </>
      ) : null}
    </section>
  );
}

export default function TeamProfileHub({
  team,
  season,
  favorite,
  finalRankingsAvailable,
  rankingsUpdated,
  recordsUpdated,
  finishesUpdated,
  availableSeasons,
  onBack,
  onSeasonChange,
  onToggleFavorite,
}) {
  const [tab, setTab] = useState(readInitialTab);
  const [scheduleFilter, setScheduleFilter] = useState('all');
  const [statsMode, setStatsMode] = useState('team');
  const [scheduleState, setScheduleState] = useState({
    loading: true,
    refreshing: false,
    data: null,
    error: '',
  });
  const [statsState, setStatsState] = useState({ loading: false, data: null, error: '' });
  const [scheduleAttempt, setScheduleAttempt] = useState(0);
  const scheduleController = useRef(null);
  const statsController = useRef(null);

  useEffect(() => {
    function syncTab() {
      const next = readInitialTab();
      setTab(next);
    }
    window.addEventListener('popstate', syncTab);
    return () => window.removeEventListener('popstate', syncTab);
  }, []);

  useEffect(() => {
    scheduleController.current?.abort();
    statsController.current?.abort();
    statsController.current = null;
    setScheduleState({ loading: true, refreshing: false, data: null, error: '' });
    setStatsState({ loading: false, data: null, error: '' });
    setScheduleFilter('all');
    const controller = new AbortController();
    scheduleController.current = controller;
    getTeamSchedule({
      season,
      team: team.slug,
      force: scheduleAttempt > 0,
      signal: controller.signal,
      onProgress: (data) => {
        if (scheduleController.current !== controller || controller.signal.aborted) return;
        setScheduleState({
          loading: false,
          refreshing: data?.pagination?.complete === false,
          data,
          error: '',
        });
      },
    })
      .then((data) => { if (scheduleController.current !== controller || controller.signal.aborted) return; setScheduleState({
        loading: false,
        refreshing: false,
        data,
        error: '',
      }); })
      .catch((error) => {
        if (scheduleController.current !== controller || controller.signal.aborted || error?.name === 'AbortError') return;
        setScheduleState((current) => current.data
          ? { ...current, loading: false, refreshing: false }
          : {
              loading: false,
              refreshing: false,
              data: null,
              error: error?.message || 'The team schedule is temporarily unavailable.',
            });
      });
    return () => {
      controller.abort();
      statsController.current?.abort();
      statsController.current = null;
    };
  }, [season, team.slug, scheduleAttempt]);

  useEffect(() => {
    if (
      statsState.data ||
      statsState.error ||
      statsController.current
    ) return undefined;

    let timer = null;
    const startRequest = () => {
      if (statsController.current || statsState.data || statsState.error) return;
      const controller = new AbortController();
      statsController.current = controller;
      setStatsState({ loading: true, data: null, error: '' });
      getTeamSeasonStats({ season, team: team.slug, signal: controller.signal })
        .then((data) => {
          if (statsController.current !== controller || controller.signal.aborted) return;
          statsController.current = null;
          setStatsState({ loading: false, data, error: '' });
        })
        .catch((error) => {
          if (statsController.current !== controller) return;
          statsController.current = null;
          if (error?.name === 'AbortError') return;
          setStatsState({ loading: false, data: null, error: error?.message || 'Season statistics are temporarily unavailable.' });
        });
    };

    if (tab === 'stats') startRequest();
    else timer = window.setTimeout(startRequest, 900);

    return () => {
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [tab, season, team.slug, statsState.data, statsState.error]);

  function changeTab(next) {
    if (!VALID_TABS.has(next)) return;
    setTab(next);
    writeTab(next);
  }

  function openMatch(game) {
    if (!game?.gameId || !game?.date) return;
    const url = new URL(window.location.href);
    url.searchParams.set('date', game.date);
    url.searchParams.delete('sport');
    url.searchParams.set('match', game.gameId);
    url.searchParams.delete('team');
    url.searchParams.delete('teamSeason');
    url.searchParams.delete('teamTab');
    url.hash = 'scores';
    window.location.assign(url.toString());
  }

  const dataUpdated = finishesUpdated || recordsUpdated || rankingsUpdated;
  const profileRecord = getRecord(team);
  const recordDetail = team?.finalRecord?.throughDate
    ? `Through ${formatDate(team.finalRecord.throughDate, true)}`
    : season === 2026 ? 'Updates as NCAA results are published' : team?.winPctLabel ? `${team.winPctLabel} win pct` : '';

  return (
    <div className="team-profile team-profile-hub">
      <div className="team-profile-toolbar">
        <button type="button" className="team-profile-back" onClick={onBack}><span aria-hidden="true">←</span> All teams</button>
        <label className="team-hub-season-select">
          <span>Season</span>
          <select value={season} onChange={(event) => onSeasonChange(Number(event.target.value))}>
            {availableSeasons.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
      </div>

      <div className="team-profile-hero">
        <div className="team-profile-logo"><TeamLogo team={team} variant="match-center" /></div>
        <div className="team-profile-heading">
          <p className="eyebrow coral">Women’s Division I</p>
          <h1>{team.name}</h1>
          {team.longName && team.longName !== team.name ? <p>{team.longName}</p> : null}
          <div className="team-hub-hero-record" aria-label={`${season} season record ${profileRecord}`}>
            <strong>{profileRecord}</strong>
            <span>{season} season</span>
            {recordDetail ? <small>{recordDetail}</small> : null}
          </div>
        </div>
        <button
          type="button"
          className={`team-profile-favorite${favorite ? ' active' : ''}`}
          aria-pressed={favorite}
          aria-label={favorite ? `Remove ${team.name} from My Teams` : `Add ${team.name} to My Teams`}
          title={favorite ? 'Remove from My Teams' : 'Add to My Teams'}
          onClick={() => onToggleFavorite(team)}
        ><span aria-hidden="true">{favorite ? '★' : '☆'}</span><span className="team-profile-favorite-label">{favorite ? 'Saved' : 'Save'}</span></button>
      </div>

      <div className="team-profile-metrics compact-profile-metrics team-hub-summary-metrics">
        <ProfileMetric
          label={season === 2026
            ? 'Current AVCA rank'
            : team?.rankingContextSeason && team.rankingContextSeason !== season
            ? `${team.rankingContextSeason} final AVCA rank`
            : 'Final AVCA rank'}
          value={getRankDisplay(team, finalRankingsAvailable)}
          detail={season === 2026
            ? rankingsUpdated ? `Poll published ${formatUpdated(rankingsUpdated)}` : 'Official AVCA poll'
            : team?.rankingContextSeason && team.rankingContextSeason !== season
            ? 'Latest available poll context'
            : rankingsUpdated ? `Updated ${formatUpdated(rankingsUpdated)}` : ''}
        />
        <ProfileMetric
          label="NCAA tournament finish"
          value={season === 2026 ? 'Season in progress' : getFinish(team)}
          detail={season === 2026 ? 'Updates when the 2026 postseason begins' : getFinishDetail(team)}
        />
      </div>

      <nav className="team-hub-tabs" aria-label="Team profile sections">
        {['overview', 'schedule', 'stats'].map((value) => (
          <button type="button" key={value} className={tab === value ? 'active' : ''} aria-current={tab === value ? 'page' : undefined} onClick={() => changeTab(value)}>
            {value === 'overview' ? 'Overview' : value === 'schedule' ? 'Schedule' : 'Stats'}
          </button>
        ))}
      </nav>

      {tab !== 'stats' && !scheduleState.loading && (scheduleState.error || scheduleState.data?.partial) ? (
        <div className="team-hub-state team-hub-recovery" role="status">
          {scheduleState.error ? 'The schedule could not be loaded. No games have been counted.' : 'Full schedule coverage could not be confirmed. Available matches are shown below.'}
          <button type="button" onClick={() => setScheduleAttempt((attempt) => attempt + 1)}>Retry schedule</button>
        </div>
      ) : null}

      {tab === 'overview' ? (
        <div className="team-hub-overview">
          <MatchPanel
            eyebrow="Recent results"
            title="Last 5 matches"
            games={scheduleState.data?.recent || []}
            loading={scheduleState.loading}
            error={scheduleState.error}
            empty="No completed matches are currently available for this season."
            onOpen={openMatch}
          />
          <MatchPanel
            eyebrow="On deck"
            title="Next 5 matches"
            games={scheduleState.data?.upcoming || []}
            loading={scheduleState.loading}
            error={scheduleState.error}
            empty="No upcoming matches are currently published for this season."
            onOpen={openMatch}
          />
          {season === 2026 && scheduleState.data?.fetchedAt ? (
            <p className="team-hub-updated">Schedule synced {formatUpdated(scheduleState.data.fetchedAt)}</p>
          ) : dataUpdated ? (
            <p className="team-hub-updated">Season data through {formatUpdated(dataUpdated)}</p>
          ) : null}
          {scheduleState.refreshing ? (
            <p className="team-hub-updated" aria-live="polite">
              Showing available games while the full schedule finishes loading…
            </p>
          ) : null}
        </div>
      ) : null}

      {tab === 'schedule' ? (
        <ScheduleTab
          schedule={scheduleState.data}
          loading={scheduleState.loading}
          error={scheduleState.error}
          filter={scheduleFilter}
          onFilter={setScheduleFilter}
          onOpen={openMatch}
        />
      ) : null}

      {tab === 'stats' ? (
        <StatsTab
          statsState={statsState}
          mode={statsMode}
          onMode={setStatsMode}
          onRetry={() => setStatsState({ loading: false, data: null, error: '' })}
          fullScheduleCount={scheduleState.data?.counts?.total || 0}
        />
      ) : null}
    </div>
  );
}
